/**
 * MagneticField.ts
 * Mô phỏng từ trường của thanh nam châm
 */

import { Point } from './ColorTracker'

export interface Vector {
    x: number
    y: number
    magnitude: number
    angle: number
}

class MagneticField {
    // Vị trí các cực
    private northPole: Point | null = null
    private southPole: Point | null = null

    // Cường độ từ trường giả định
    private strength = 5000

    /**
     * Cập nhật vị trí các cực nam châm
     */
    updatePoles(north: Point | null, south: Point | null) {
        this.northPole = north
        this.southPole = south
    }

    /**
     * Tính toán vector cảm ứng từ B tại điểm (x, y)
     * Dựa trên mô hình 2 đơn cực từ (monopoles)
     * B = B_north + B_south
     * B_north hướng ra xa, B_south hướng vào trong
     */
    getFieldAt(x: number, y: number): Vector {
        if (!this.northPole || !this.southPole) {
            return { x: 0, y: 0, magnitude: 0, angle: 0 }
        }

        // Tính B do cực Bắc (N) gây ra tại (x,y)
        // Vector hướng từ N -> (x,y)
        const dxN = x - this.northPole.x
        const dyN = y - this.northPole.y
        const distN2 = dxN * dxN + dyN * dyN // r^2
        const distN = Math.sqrt(distN2)      // r

        // Tránh chia cho 0
        const rN = Math.max(distN, 10)

        // Độ lớn B_N tỉ lệ nghịch với r^2
        const magN = this.strength / (rN * rN)

        // Vector đơn vị
        const uNx = dxN / rN
        const uNy = dyN / rN

        // Vector B_N
        const Bnx = uNx * magN
        const Bny = uNy * magN

        // Tính B do cực Nam (S) gây ra tại (x,y)
        // Vector hướng từ (x,y) -> S (hút vào)
        const dxS = this.southPole.x - x
        const dyS = this.southPole.y - y
        const distS2 = dxS * dxS + dyS * dyS
        const distS = Math.sqrt(distS2)

        const rS = Math.max(distS, 10)
        const magS = this.strength / (rS * rS)

        const uSx = dxS / rS
        const uSy = dyS / rS

        const Bsx = uSx * magS
        const Bsy = uSy * magS

        // Tổng hợp vector: B = B_N + B_S
        const Bx = Bnx + Bsx
        const By = Bny + Bsy

        const magnitude = Math.sqrt(Bx * Bx + By * By)
        const angle = Math.atan2(By, Bx)

        return {
            x: Bx,
            y: By,
            magnitude,
            angle
        }
    }

    /**
     * Tạo danh sách các điểm trên đường sức từ (Field Line) xuất phát từ 1 điểm gốc
     * Dùng phương pháp Euler integration: P_new = P_old + B_vector * step
     * 
     * @param stepSize 
     * - Dương (>0): Di chuyển theo chiều vector B (N -> S)
     * - Âm (<0): Di chuyển ngược chiều vector B (Từ S về N)
     */
    traceFieldLine(startX: number, startY: number, stepSize: number = 10, maxSteps: number = 200): Point[] {
        const path: Point[] = [{ x: startX, y: startY }]
        let currentX = startX
        let currentY = startY

        // Xác định cực mục tiêu để dừng vẽ
        // Nếu stepSize > 0 (đi xuôi từ N), mục tiêu là S
        // Nếu stepSize < 0 (đi ngược từ S), mục tiêu là N
        const targetPole = stepSize > 0 ? this.southPole : this.northPole
        const startPole = stepSize > 0 ? this.northPole : this.southPole

        for (let i = 0; i < maxSteps; i++) {
            const B = this.getFieldAt(currentX, currentY)

            // Nếu từ trường quá yếu hoặc quá mạnh (gần cực), dừng lại
            if (B.magnitude < 0.05 || B.magnitude > 500) break

            // Di chuyển theo hướng vector B (nhân stepSize để kiểm soát hướng)
            const moveX = Math.cos(B.angle) * stepSize
            const moveY = Math.sin(B.angle) * stepSize

            currentX += moveX
            currentY += moveY

            path.push({ x: currentX, y: currentY })

            // Logic dừng:
            // 1. Chạm cực đích
            if (targetPole) {
                const distToTarget = Math.sqrt((currentX - targetPole.x) ** 2 + (currentY - targetPole.y) ** 2)
                if (distToTarget < 20) break
            }

            // 2. Quay lại quá gần điểm xuất phát (nếu không phải bước đầu)
            if (startPole && i > 5) {
                const distToStart = Math.sqrt((currentX - startPole.x) ** 2 + (currentY - startPole.y) ** 2)
                if (distToStart < 15) break
            }

            // 3. Ra quá xa khung hình (tùy chọn, để giảm tính toán)
            if (Math.abs(currentX - startX) > 2000 || Math.abs(currentY - startY) > 2000) break
        }

        return path
    }
}

export default new MagneticField()
